import {
  EVENT_SLUG_MAX_LENGTH,
  EVENT_SLUG_MIN_LENGTH,
  HIIVE_SLUG_MAX_LENGTH,
  HIIVE_SLUG_MIN_LENGTH,
} from "@/lib/handles";
import { isValidAssistantHandle } from "./assistant-handles";

export type MentionType = "user" | "hiive" | "event" | "assistant" | "unknown";

export type MentionMatch = {
  startIndex: number;
  endIndex: number;
  handle: string;
  eventSlug?: string;
  text: string;
};

export type ResolvedMention = MentionMatch & {
  type: MentionType;
  resolved: boolean;
  entityId?: string;
  hiiveId?: number;
  eventId?: number;
};

export type MentionSegment =
  | { kind: "text"; value: string }
  | { kind: "mention"; value: string; mention: ResolvedMention };

export function extractMentionMatches(text: string): MentionMatch[] {
  if (!text) {
    return [];
  }

  const matches: MentionMatch[] = [];
  const length = text.length;
  let index = 0;

  while (index < length) {
    if (text[index] !== "@") {
      index++;
      continue;
    }

    const startIndex = index;
    const cursor = index + 1;
    if (cursor >= length) {
      index++;
      continue;
    }

    const firstChar = text[cursor];
    if (!isAccountStartChar(firstChar)) {
      index++;
      continue;
    }

    let handleEnd = cursor + 1;
    while (handleEnd < length && isAccountContinuationChar(text[handleEnd])) {
      handleEnd++;
    }

    const handleRaw = text.slice(cursor, handleEnd);
    const handle = handleRaw.toLowerCase();

    if (
      handle.length < HIIVE_SLUG_MIN_LENGTH ||
      (handle.length > HIIVE_SLUG_MAX_LENGTH && !isValidAssistantHandle(handle))
    ) {
      index++;
      continue;
    }

    let endIndex = handleEnd;
    let eventSlug: string | undefined;
    if (handleEnd < length && text[handleEnd] === "/") {
      const slugStart = handleEnd + 1;
      let slugCursor = slugStart;
      while (slugCursor < length && isEventSlugChar(text[slugCursor])) {
        slugCursor++;
      }

      const slugRaw = text.slice(slugStart, slugCursor);
      const slug = slugRaw.toLowerCase();

      if (
        slug.length >= EVENT_SLUG_MIN_LENGTH &&
        slug.length <= EVENT_SLUG_MAX_LENGTH
      ) {
        eventSlug = slug;
        endIndex = slugCursor;
      } else {
        endIndex = handleEnd;
      }
    }

    const safeEnd = Math.max(endIndex, startIndex + 1);
    matches.push({
      startIndex,
      endIndex: safeEnd,
      handle,
      eventSlug,
      text: text.slice(startIndex, safeEnd),
    });

    index = safeEnd;
  }

  return matches;
}

export function matchesToUnresolvedMentions(
  matches: MentionMatch[],
): ResolvedMention[] {
  return matches.map((match) => ({
    ...match,
    type: match.eventSlug ? "event" : "unknown",
    resolved: false,
  }));
}

export function segmentTextByMentions(
  text: string,
  mentions: ResolvedMention[] | undefined,
): MentionSegment[] {
  if (!mentions?.length || text.length === 0) {
    return [{ kind: "text", value: text }];
  }

  const sorted = [...mentions]
    .filter(
      (mention) =>
        mention.startIndex != null &&
        mention.endIndex != null &&
        mention.startIndex >= 0 &&
        mention.endIndex >= 0,
    )
    .sort((a, b) => a.startIndex - b.startIndex);

  const segments: MentionSegment[] = [];
  let cursor = 0;

  for (const mention of sorted) {
    const start = clamp(mention.startIndex, 0, text.length);
    const end = clamp(Math.max(mention.endIndex, start + 1), 0, text.length);

    if (end <= start) {
      continue;
    }

    if (cursor < start) {
      segments.push({ kind: "text", value: text.slice(cursor, start) });
    }

    const snippet = text.slice(start, end);
    if (snippet.length > 0) {
      segments.push({ kind: "mention", value: snippet, mention });
    }

    cursor = end;
  }

  if (cursor < text.length) {
    segments.push({ kind: "text", value: text.slice(cursor) });
  }

  if (segments.length === 0) {
    return [{ kind: "text", value: text }];
  }

  return segments;
}

export function resolveMentionHref(mention: ResolvedMention): string | null {
  if (!mention.resolved) {
    return null;
  }

  switch (mention.type) {
    case "user":
      return `/profile/${mention.handle}`;
    case "hiive":
      return `/hiives/${mention.handle}`;
    case "event":
      return mention.eventSlug
        ? `/hiives/${mention.handle}/events/${mention.eventSlug}`
        : null;
    default:
      return null;
  }
}

export function mentionColorClass(type: MentionType) {
  switch (type) {
    case "user":
      return "text-brand";
    case "hiive":
      return "text-accent";
    case "event":
      return "text-info";
    default:
      return "text-brand";
  }
}

function isAccountStartChar(char: string): boolean {
  return isLetter(char);
}

function isAccountContinuationChar(char: string): boolean {
  return isLetter(char) || isDigit(char) || char === "." || char === "_";
}

function isEventSlugChar(char: string): boolean {
  return isLetter(char) || isDigit(char) || char === "-" || char === "_";
}

function isLetter(char: string): boolean {
  if (!char) {
    return false;
  }
  const code = char.charCodeAt(0);
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

function isDigit(char: string): boolean {
  if (!char) {
    return false;
  }
  const code = char.charCodeAt(0);
  return code >= 48 && code <= 57;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}
